/**
 * Per-flavor brand identity.
 *
 * mint24.app / mint24.net serve the Russian product: Cyrillic wordmark,
 * Telegram support, Yandex.Metrika. getmintai.app is the US surface operated
 * by Elvo LLC and must carry none of that — it doubles as the business website
 * backing the company's US banking application, so a stray Russian asset or
 * link there is a real-world problem, not a cosmetic one.
 *
 * Everything host-dependent lives here rather than being spread across
 * components, so "does the US surface still leak anything Russian?" is a
 * question you answer by reading one file.
 */

import type { SiteFlavor } from "@/lib/site-flavor";

/** Светлая и тёмная версия одной картинки. */
type ThemedArt = { light: string; dark: string };

/**
 * Мокап под каждую локаль. Телефоны на лендинге — это снимки интерфейса, а не
 * абстрактная графика: на английской странице на них должен быть английский
 * экран, иначе страница обещает одно, а показывает другое.
 */
type LocalizedArt = { ru: ThemedArt; en: ThemedArt };

export interface Brand {
  /** Product name in copy, OG siteName and legal documents. */
  name: string;
  /** Canonical host, without protocol. */
  domain: string;
  /**
   * Landing wordmark per locale. Keyed by flavor first and locale second: the
   * mint24 `ru` wordmark is Cyrillic ("МИНТ24"), so locale must never be able
   * to pull it onto the US surface on its own.
   *
   * A plain record, not a function, so a Brand can cross the server/client
   * boundary as a prop — see the note on BRANDS.
   */
  wordmarks: Record<"ru" | "en", string>;
  /** Support mailbox, or null when support runs through a chat channel instead. */
  supportEmail: string | null;
  /**
   * Chat entry point for support, or null on surfaces that must not link out to
   * one. Named for the role, not the vendor: this object is serialised into
   * every page, so a field called `telegramSupportUrl` puts the word "Telegram"
   * in getmintai.app's view-source even though its value there is null.
   */
  supportChatUrl: string | null;
  /**
   * Whether a messenger appears as a product surface (export target, format
   * label). Separate from `supportChatUrl`: a surface could plausibly drop chat
   * support while keeping the export, so the two are not one flag.
   *
   * On mint24 the messenger is Telegram; getmintai has none.
   */
  messengerFeatures: boolean;
  /**
   * When the full-screen onboarding tour runs.
   *
   * - `before-landing` — mint24's behaviour: a first-time visitor gets the tour
   *   instead of the landing, and finishing it sends them to sign-up.
   * - `on-cta` — the landing always renders; the tour opens when the visitor
   *   presses a "try it" button, and finishing it sends them to sign-up. Never
   *   shown after sign-in: here the tour is a pitch, not a product walkthrough.
   * - `off` — no tour anywhere.
   *
   * getmintai.app is `off` only until its tour art is English. Every image the
   * tour renders is currently a Russian screenshot, so pressing "try free"
   * would open exactly what this surface exists to avoid. The flow itself is
   * built and tested; this is one word away from `on-cta`.
   */
  onboardingTour: "before-landing" | "on-cta" | "off";
  /**
   * Папки с картинками тура, по локали. Файлы в туре — это скриншоты
   * интерфейса, поэтому они привязаны к языку страницы, а не к бренду: на
   * mint24.app/ru нужны русские экраны, на mint24.app/en — английские.
   *
   * Раньше путь был один на бренд, и mint24 показывал русские скриншоты обеим
   * локалям; до этого путь был один на всех, и английский набор перезаписал
   * русский прямо в `public/onboarding/`. Корень `public/onboarding/` — русский
   * набор, `public/onboarding/en/` — английский; общей папки нет намеренно.
   *
   * В английском наборе пока нет своих `onb-carousel-preview.webp` и
   * `onb-stories-preview.png` — там лежат копии русских. Слайды карусели и
   * сторис на английской локали остаются русскими, пока не снимут замену.
   */
  onboardingArt: { ru: string; en: string };
  /**
   * Whether the "install on phone" guide is offered.
   *
   * Off on the US surface. Its eight screenshots are unusable there: they show
   * mint24.app in the address bar, the Cyrillic МИНТ24 wordmark, a Russian app
   * UI ("Выберите формат", "Пост в Telegram"), Russian system menus including
   * "Открыть в Yandex" and "Отправить Алисе AI", and the RF legal notice
   * declaring Meta an extremist organisation. Re-shooting them needs a phone and
   * an authenticated session; until then the entry point is hidden rather than
   * shipped half-translated.
   */
  installGuide: boolean;
  /**
   * Language the generator assumes when it has nothing else to go on.
   *
   * Four call sites defaulted to "ru" — the detected-language fallback, the
   * options dialog, the batch generator and languageNameToCode. Correct for
   * mint24, wrong for a US product: a visitor who typed English could still get
   * a Russian carousel back. Keyed by brand rather than locale so mint24's
   * behaviour is unchanged for every one of its users.
   */
  defaultContentLanguage: "ru" | "en";
  /** Legal operator shown in the footer and legal pages. */
  legalEntity: string;
  /** Registered mailing address, used in the privacy policy and terms. */
  legalAddress: string | null;
  /** Jurisdiction governing the terms of service. */
  legalJurisdiction: string | null;
  /**
   * Yandex.Metrika is a Russian analytics vendor — off on the US surface.
   *
   * Server-only: read in the root layout to decide whether to emit the tag.
   * Deliberately outside {@link ClientBrand} so the vendor's name never reaches
   * a page payload.
   */
  yandexMetrika: boolean;
  /**
   * Link-preview card, as a static file.
   *
   * This used to be `src/app/opengraph-image.tsx`, and the file convention broke
   * it two ways: Next also emits `og:image:alt` from that module's static `alt`
   * export, so getmintai.app advertised "Mint24 — приложение для создания
   * каруселей"; and the route has no file extension, so middleware
   * locale-redirected it and `/opengraph-image` answered with HTML instead of a
   * PNG on every domain — every shared link had a broken preview. Static files
   * are matched out of middleware and are per-flavor by construction.
   * Regenerate both with scripts/build-mintai-logo.py.
   */
  ogCard: { url: string; alt: string };
  /**
   * Product screenshots for the landing, or null when none exist for this brand.
   *
   * These are baked PNGs of the app UI. The mint24 set shows the Russian
   * interface ("Перегенерировать", "Пост в Telegram", a Russian flag in the
   * language row); the getmintai set is built from the same phone frame with
   * English screens by `scripts/build-mintai-mockups.py` (see that file for how,
   * and `src/app/[locale]/test-visual/mockup-screens` for what it captures).
   */
  mockups: {
    hero: LocalizedArt | null;
    editor: LocalizedArt | null;
    /** Used as schema.org `screenshot`; omitted entirely when null. */
    schemaScreenshot: { ru: string | null; en: string | null };
  };
}

/** Папка с артом тура для локали; всё кроме `ru` считаем английским. */
export function onboardingArtFor(
  brand: Pick<Brand, "onboardingArt">,
  locale: string,
): string {
  return locale === "ru" ? brand.onboardingArt.ru : brand.onboardingArt.en;
}

/**
 * Мокапы лендинга для локали; всё кроме `ru` считаем английским.
 *
 * `hero`/`editor` могут отсутствовать целиком — тогда лендинг рисуется без
 * телефонов, и это лучше, чем подставить снимок другого бренда.
 */
export function mockupsFor(brand: Pick<Brand, "mockups">, locale: string) {
  const lang = locale === "ru" ? "ru" : "en";
  const { hero, editor, schemaScreenshot } = brand.mockups;
  return {
    hero: hero ? hero[lang] : null,
    editor: editor ? editor[lang] : null,
    schemaScreenshot: schemaScreenshot[lang],
  };
}

/** Wordmark for a locale, falling back to the English mark. */
export function wordmarkFor(brand: Pick<Brand, "wordmarks">, locale: string): string {
  return locale === "ru" ? brand.wordmarks.ru : brand.wordmarks.en;
}

/** `mailto:` for the support mailbox, or null when the flavor has no email support. */
export function supportMailto(brand: Pick<Brand, "supportEmail">): string | null {
  return brand.supportEmail ? `mailto:${brand.supportEmail}` : null;
}

/**
 * Fields of a Brand that client components are allowed to read.
 *
 * The provider hands this to the browser, and next serialises it into the RSC
 * payload of every page — so whatever is listed here is readable in view-source.
 * Everything omitted (`domain`, `legalJurisdiction`, `yandexMetrika`, `ogCard`)
 * is consumed on the server only, and leaving it out is what keeps
 * "yandexMetrika" out of the US surface's HTML. Adding a field here is a
 * deliberate act: check what its name and value say about the surface first.
 */
const CLIENT_BRAND_FIELDS = [
  "name",
  "wordmarks",
  "supportEmail",
  "supportChatUrl",
  "messengerFeatures",
  "onboardingTour",
  "onboardingArt",
  "installGuide",
  "defaultContentLanguage",
  "legalEntity",
  "legalAddress",
  // landing-hero.tsx and landing-showcase-carousel.tsx pick light/dark art at
  // render time from `resolvedTheme`, so the paths have to reach the browser.
  // getmintai's are the English captures; schemaScreenshot rides along unused
  // and is null there.
  "mockups",
] as const;

export type ClientBrand = Pick<Brand, (typeof CLIENT_BRAND_FIELDS)[number]>;

/** Narrow a Brand to the fields safe to serialise into a page. */
export function clientBrand(brand: Brand): ClientBrand {
  const out = {} as Record<string, unknown>;
  for (const key of CLIENT_BRAND_FIELDS) out[key] = brand[key];
  return out as ClientBrand;
}
